  <template>
    <div class="barcode-scanner">
      <div v-if="showScanner" class="scanner-fullscreen">
        <div class="scanner-header">
          <button @click="closeScanner" class="close-btn">×</button>
        </div>
        
        <div class="scanner-content">
          <div class="scanner-area" ref="scannerArea" id="scanner">
            <div class="scanner-overlay">
              <div class="scanner-frame"></div>
            </div>
            <button @click="closeScanner" class="scanner-close-btn">×</button>
          </div>
        </div>
      </div>
  
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-spinner"></div>
      </div>
    </div>
  </template>
  
  <script>
  import { mapGetters, mapActions } from 'vuex'
  import { Html5Qrcode, Html5QrcodeScanType } from 'html5-qrcode'
  
  export default {
    name: 'BarcodeScanner',
    data() {
      return {
        showScanner: false,
        isLoading: false,
        isScanning: false,
        qrCodeScanner: null,
        lastDetectAt: 0,
        throttleMs: 200,
        scanFailureCount: 0,
        isLiffEnvironment: false,
      }
    },
    computed: {
      ...mapGetters('config', ['userProfile']),
      ...mapGetters('cart', ['cart']),
      isLiffApp() {
        return this.isLiffEnvironment && typeof liff !== 'undefined' && liff.isInClient()
      }
    },
    methods: {
      ...mapActions('config', ['setProducts']),

      async sendLog(level, tag, message, data = null) {
        try {
          await this.$axios.post(`${process.env.MAIN_API}/b2b/log`, {
            level,
            tag,
            message,
            data,
            timestamp: new Date().toISOString()
          })
        } catch (err) {
          console.error('Failed to send log to server:', err)
        }
      },
  
      openScanner() {
        if (this.showScanner) {
          this.closeScanner()
          return
        }

        this.isScanning = false
        this.scanFailureCount = 0
        this.showScanner = true
        this.$nextTick(() => {
          try { document.documentElement.style.overflow = 'hidden'; document.body.style.overflow = 'hidden' } catch(_){}
          this.initScanner()
        })
      },
  
      closeScanner() {
        this.showScanner = false
        this.stopScanner()
        try { document.documentElement.style.overflow = ''; document.body.style.overflow = '' } catch(_){}
      },
  
      isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
               (window.innerWidth <= 768)
      },

      async requestCameraPermission() {
        try {
          if (navigator.permissions && navigator.permissions.query) {
            try {
              const permissionStatus = await navigator.permissions.query({ name: 'camera' })
              if (permissionStatus.state === 'denied') {
                this.sendLog('info', 'SCANNER', 'Camera permission already denied')
                return false
              }
              if (permissionStatus.state === 'granted') {
                this.sendLog('info', 'SCANNER', 'Camera permission already granted')
                return true
              }
            } catch (e) {
              this.sendLog('debug', 'SCANNER', 'Permission query not supported', { error: e.message })
            }
          }
          
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: "environment" } 
          })
          stream.getTracks().forEach(track => track.stop())
          this.sendLog('info', 'SCANNER', 'Camera permission granted')
          return true
        } catch (error) {
          this.sendLog('error', 'SCANNER', 'Camera permission denied', { 
            error: error.message,
            name: error.name 
          })
          return false
        }
      },

      isNotAllowedError(error) {
        const errorName = error?.name || ''
        const errorMessage = error?.message || String(error) || ''
        return errorName === 'NotAllowedError' || 
               errorName === 'PermissionDeniedError' ||
               errorMessage.includes('NotAllowedError') ||
               errorMessage.includes('permission denied') ||
               errorMessage.includes('not allowed by the user')
      },

      async initScanner() {
        try {
          const viewportWidth = window.innerWidth
          const viewportHeight = window.innerHeight
          const isMobile = this.isMobileDevice()
          
          const deviceWidth = viewportWidth
          const deviceHeight = viewportHeight - 120
          
          this.sendLog('info', 'SCANNER', 'Viewport size', { 
            viewportWidth, 
            viewportHeight,
            deviceWidth,
            deviceHeight,
            isMobile,
            isLiff: this.isLiffApp 
          })
          
          const config = {
            fps: isMobile ? 20 : 30,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              return {
                width: viewfinderWidth,
                height: viewfinderHeight
              }
            },
            supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
            showTorchButtonIfSupported: false,
            showZoomSliderIfSupported: false,
            showFullscreenButtonIfSupported: false,
            disableFlip: false,
            rememberLastUsedCamera: true
          }

          if (isMobile) {
            const hasPermission = await this.requestCameraPermission()
            if (!hasPermission) {
              throw new DOMException('Camera permission denied', 'NotAllowedError')
            }
          }

          this.qrCodeScanner = new Html5Qrcode(this.$refs.scannerArea.id || 'scanner', false)
          
          let cameraIdOrConfig
          if (isMobile) {
            cameraIdOrConfig = {
              facingMode: "environment",
              width: { ideal: Math.min(deviceWidth * 2, 1920), min: 640 },
              height: { ideal: Math.min(deviceHeight * 2, 1080), min: 480 },
              focusMode: "continuous"
            }
          } else {
            cameraIdOrConfig = {
              facingMode: "environment",
              width: { ideal: Math.min(deviceWidth, 1920), min: 1280 },
              height: { ideal: Math.min(deviceHeight, 1080), min: 720 },
              focusMode: "continuous"
            }
          }

          await this.qrCodeScanner.start(
            cameraIdOrConfig,
            config,
            this.onScanSuccess.bind(this),
            this.onScanFailure.bind(this)
          )
          
          this.isScanning = true
          
        } catch (error) {
          const errorDetails = {
            message: error?.message || String(error),
            name: error?.name || 'UnknownError',
            stack: error?.stack,
            toString: String(error),
            isMobile: this.isMobileDevice(),
            userAgent: navigator.userAgent,
            protocol: window.location.protocol,
            isHttps: window.location.protocol === 'https:'
          }
          
          try {
            Object.keys(error).forEach(key => {
              if (!errorDetails[key]) {
                errorDetails[key] = error[key]
              }
            })
          } catch (e) {}
          
          this.sendLog('error', 'SCANNER', 'Scanner initialization failed', errorDetails)
          
          const isNotAllowed = this.isNotAllowedError(error)
          const isNotFound = error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError'
          
          if (this.isMobileDevice() && !isNotAllowed && !isNotFound) {
            try {
              this.sendLog('info', 'SCANNER', 'Trying fallback config for mobile')
              await this.initScannerFallback()
              return
            } catch (fallbackError) {
              const fallbackErrorDetails = {
                message: fallbackError?.message || String(fallbackError),
                name: fallbackError?.name || 'UnknownError',
                stack: fallbackError?.stack,
                originalError: errorDetails
              }
              this.sendLog('error', 'SCANNER', 'Fallback also failed', fallbackErrorDetails)
              if (!error?.message && !error?.name && fallbackError?.message) {
                error = fallbackError
              }
            }
          }
          
          const errorName = error?.name || 'UnknownError'
          const errorMessage = error?.message || String(error) || '不明なエラー'
          
          if (isNotAllowed) {
            const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
            const isChrome = /CriOS|Chrome/i.test(navigator.userAgent)
            let message = 'カメラへのアクセスが拒否されました。'
            
            if (isIOS && isChrome) {
              message += '\n\niOS Chromeでは、設定 > Chrome > カメラで許可を有効にしてください。\nまたは、Safariブラウザでお試しください。'
            } else if (isIOS) {
              message += '\n\n設定 > Safari > カメラで許可を有効にしてください。'
            } else {
              message += '\n\nブラウザの設定でカメラの許可を有効にしてください。'
            }
            
            this.$alert(message, 'カメラアクセス拒否', {
              confirmButtonText: 'OK',
              type: 'error'
            })
          } else if (errorName === 'NotFoundError') {
            this.$alert('カメラが見つかりませんでした。デバイスにカメラが接続されているか確認してください。', 'カメラ未発見', {
              confirmButtonText: 'OK',
              type: 'error'
            })
          } else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
            this.$alert('カメラが他のアプリケーションで使用中の可能性があります。他のアプリを閉じてから再度お試しください。', 'カメラ使用中', {
              confirmButtonText: 'OK',
              type: 'error'
            })
          } else if (errorName === 'OverconstrainedError' || errorName === 'ConstraintNotSatisfiedError') {
            this.$alert('カメラの設定がサポートされていません。別のブラウザでお試しください。', 'カメラ設定エラー', {
              confirmButtonText: 'OK',
              type: 'error'
            })
          } else {
            const isMobile = this.isMobileDevice()
            const isHttps = window.location.protocol === 'https:'
            const details = []
            
            if (isMobile && !isHttps) {
              details.push('HTTPS接続が必要です')
            }
            if (isMobile) {
              details.push('モバイルブラウザの互換性の問題の可能性があります')
            }
            if (errorMessage && errorMessage !== '不明なエラー') {
              details.push(`詳細: ${errorMessage}`)
            }
            
            const alertMessage = `スキャナーの初期化に失敗しました。${details.length > 0 ? '\n\n' + details.join('\n') : ''}`
            
            this.$alert(alertMessage, 'エラー', {
              confirmButtonText: 'OK',
              type: 'error'
            })
          }
          this.closeScanner()
        }
      },

      async initScannerFallback() {
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const deviceWidth = viewportWidth
        const deviceHeight = viewportHeight - 120
        
        const config = {
          fps: 15,
          qrbox: { 
            width: Math.min(deviceWidth * 0.9, 400),
            height: Math.min(deviceHeight * 0.75, 300)
          },
          supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
          showTorchButtonIfSupported: false,
          showZoomSliderIfSupported: false,
          showFullscreenButtonIfSupported: false,
          disableFlip: false
        }

        this.qrCodeScanner = new Html5Qrcode(this.$refs.scannerArea.id || 'scanner')
        
        const strategies = [
          { facingMode: "environment" },
          { facingMode: "user" },
          async () => {
            try {
              const devices = await navigator.mediaDevices.enumerateDevices()
              const videoDevices = devices.filter(device => device.kind === 'videoinput')
              if (videoDevices.length > 0) {
                const backCamera = videoDevices.find(d => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('rear'))
                if (backCamera) {
                  return backCamera.deviceId
                }
                return videoDevices[0].deviceId
              }
            } catch (e) {
              this.sendLog('debug', 'SCANNER', 'Failed to enumerate devices', { error: e.message })
            }
            return null
          }
        ]

        let lastError = null
        
        for (let i = 0; i < strategies.length; i++) {
          try {
            let cameraIdOrConfig = strategies[i]
            
            if (typeof cameraIdOrConfig === 'function') {
              const deviceId = await cameraIdOrConfig()
              if (deviceId) {
                cameraIdOrConfig = deviceId
                this.sendLog('info', 'SCANNER', `Fallback strategy ${i + 1}: Using camera ID`, { deviceId })
              } else {
                this.sendLog('debug', 'SCANNER', `Fallback strategy ${i + 1}: No camera device found`)
                lastError = new Error('No camera device found')
                continue
              }
            } else {
              this.sendLog('info', 'SCANNER', `Fallback strategy ${i + 1}: Using config`, { config: cameraIdOrConfig })
            }

            await this.qrCodeScanner.start(
              cameraIdOrConfig,
              config,
              this.onScanSuccess.bind(this),
              this.onScanFailure.bind(this)
            )
            
            this.isScanning = true
            this.sendLog('info', 'SCANNER', `Fallback strategy ${i + 1} succeeded`)
            return
          } catch (strategyError) {
            lastError = strategyError
            this.sendLog('debug', 'SCANNER', `Fallback strategy ${i + 1} failed`, { 
              error: strategyError?.message || String(strategyError),
              name: strategyError?.name 
            })
          }
        }
        
        if (lastError) {
          throw lastError
        } else {
          throw new Error('All fallback strategies failed')
        }
      },

      async stopScanner() {
        if (this.qrCodeScanner) {
          try {
            await this.qrCodeScanner.stop()
            this.qrCodeScanner.clear()
            this.qrCodeScanner = null
          } catch (error) {
            console.error('Error stopping scanner:', error)
          }
        }
        this.isScanning = false
      },

      onScanSuccess(decodedText, decodedResult) {
        const now = Date.now()
        if (now - this.lastDetectAt < this.throttleMs) return
        
        if (!decodedText || decodedText.trim().length === 0) {
          return
        }
        
        this.scanFailureCount = 0
        this.lastDetectAt = now
        
        this.sendLog('debug', 'SCANNER', 'Barcode detected', { 
          text: decodedText.substring(0, 20),
          length: decodedText.length 
        })
        
        this.searchProduct(decodedText.trim())
      },

      onScanFailure(error) {
        if (error && typeof error === 'string' && !error.includes('No QR code found') && !error.includes('NotFoundException')) {
          this.scanFailureCount++
          if (this.scanFailureCount % 100 === 0) {
            this.sendLog('debug', 'SCANNER', 'Scan failure (logged every 100th)', { 
              error,
              failureCount: this.scanFailureCount 
            })
          }
        }
      },
  
      async searchProduct(janCode) {
        this.isLoading = true
        try {
          const response = await this.$axios.get(`${process.env.MAIN_API}/b2b/products/${this.userProfile.shop_code}?jan=${janCode}&getAll=true`)
          
          if (response.data.status === 'success' && Array.isArray(response.data.products) && response.data.products.length > 0) {
            const foundProduct = response.data.products[0]
            const cartItem = Array.isArray(this.cart) ? this.cart.find(c => c.product_id == foundProduct.product_id) : null
            const normalized = {
              ...foundProduct,
              view: true,
              popup: false,
              lock: false,
              quantity: (cartItem && cartItem.quantity) ? cartItem.quantity : (foundProduct.quantity || 0),
              from_scan: true
            }
            await this.setProducts([ normalized ])
            
            this.$alert(`商品が見つかりました: ${foundProduct.product_name}`, '成功', {
              confirmButtonText: 'OK',
              type: 'success'
            })
            this.stopScanner()
            this.closeScanner()
            
          } else {
            this.$alert('該当する商品が見つかりませんでした。', 'エラー', {
              confirmButtonText: 'OK',
              type: 'warning'
            })
            
            this.stopScanner()
            this.closeScanner()
          }
        } catch (error) {
          this.$alert('商品検索中にエラーが発生しました。', 'エラー', {
            confirmButtonText: 'OK',
            type: 'error'
          })
          
          this.stopScanner()
          this.closeScanner()
        } finally {
          this.isLoading = false
        }
      },

      checkLiffEnvironment() {
        try {
          if (typeof liff !== 'undefined' && liff) {
            this.isLiffEnvironment = true
          } else {
            this.isLiffEnvironment = false
          }
        } catch (error) {
          this.isLiffEnvironment = false
        }
      },
    },

    mounted() {
      this.checkLiffEnvironment()
    },
  
    beforeDestroy() {
      this.stopScanner()
    }
  }
  </script>
  
<style scoped>
  .barcode-scanner {
    position: relative;
  }
  
  .scanner-fullscreen {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 60px;
    background-color: #000;
    z-index: 999999;
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: calc(100vh - 120px);
  }
  
  .scanner-header {
    background-color: var(--mainColor);
    color: white;
    padding: 15px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000001;
    height: 60px;
  }

  .close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .scanner-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0;
    position: relative;
    height: 100%;
    margin-top: 0;
  }
  
  .scanner-area {
    flex: 1;
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: #000;
    width: 100%;
    height: 100%;
    z-index: 1;
    overflow: hidden;
  }

  .scanner-overlay {
    z-index: 3;
  }

  .scanner-close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    font-size: 24px;
    cursor: pointer;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  }

  .scanner-close-btn:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  ::v-deep(#scanner) {
    width: 100% !important;
    height: 100% !important;
    position: relative !important;
    min-width: 100% !important;
    min-height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
  }

  ::v-deep(#scanner video) {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: cover !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    z-index: 1 !important;
  }

  ::v-deep(#scanner canvas) {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    z-index: 2 !important;
  }

  ::v-deep(#scanner > div) {
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    position: relative !important;
  }

  ::v-deep(#scanner *) {
    box-sizing: border-box !important;
  }

  @media screen and (max-width: 768px) {
    ::v-deep(#scanner video) {
      width: 100vw !important;
      height: calc(100vh - 120px) !important;
      min-width: 100vw !important;
      min-height: calc(100vh - 120px) !important;
    }
    
    ::v-deep(#scanner canvas) {
      width: 100vw !important;
      height: calc(100vh - 120px) !important;
      min-width: 100vw !important;
      min-height: calc(100vh - 120px) !important;
    }
    
    .scanner-close-btn {
      width: 45px;
      height: 45px;
      font-size: 20px;
      top: 15px;
      right: 15px;
    }
  }

  ::v-deep(#scanner #html5-qrcode-button-camera-permission),
  ::v-deep(#scanner #html5-qrcode-button-camera-start),
  ::v-deep(#scanner #html5-qrcode-button-camera-stop),
  ::v-deep(#scanner #html5-qrcode-button-camera-change) {
    display: none !important;
  }
  
  .scanner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  
  .scanner-frame {
    width: 300px;
    height: 200px;
    border: 3px solid #00ff00;
    border-radius: 12px;
    position: relative;
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.5);
  }
  
  .scanner-frame::before,
  .scanner-frame::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border: 3px solid #00ff00;
  }
  
  .scanner-frame::before {
    top: -3px;
    left: -3px;
    border-right: none;
    border-bottom: none;
  }
  
  .scanner-frame::after {
    bottom: -3px;
    right: -3px;
    border-left: none;
    border-top: none;
  }
  
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 1001;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
  }
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid var(--mainColor);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .loading-overlay p {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
  }
  
  @media (max-width: 768px) {
    .scanner-frame {
      width: 200px;
      height: 120px;
    }
  }
</style>